import { useCallback, useEffect, useState } from 'react'
import { repository } from '@/api/repository'
import type { Incident, WorkOrder } from '@/api/types'

interface State {
  items: WorkOrder[]
  loading: boolean
  /** Relectura sobre filas que ya están en pantalla: no las hace desaparecer. */
  refreshing: boolean
  error: string | null
}

const EMPTY: State = { items: [], loading: false, refreshing: false, error: null }

/**
 * Las órdenes se traen al seleccionar la incidencia, no en el listado: el
 * camino de dos pasos (propiedades → `owner` → tarea) cuesta una llamada por
 * orden y no tiene sentido pagarlo por cada tarjeta de la lista.
 */
export function useWorkOrders(incident: Incident | null) {
  const [state, setState] = useState<State>(EMPTY)
  const [reloadKey, setReloadKey] = useState(0)

  const incidentId = incident?._id ?? null
  const otCount = incident?.otPropertyIds.length ?? 0

  useEffect(() => {
    if (!incidentId || otCount === 0) {
      setState(EMPTY)
      return
    }

    const ac = new AbortController()
    let alive = true
    // Las filas que ya están se mantienen: en un refresco, vaciarlas y volver a
    // dibujarlas hace parpadear la sección justo después de que el usuario tocó
    // una fila. La primera carga sí arranca vacía, y ahí manda el skeleton.
    setState((s) =>
      s.items.length > 0
        ? { ...s, refreshing: true, error: null }
        : { items: [], loading: true, refreshing: false, error: null },
    )

    repository
      .loadWorkOrders(incident!, ac.signal)
      .then((items) => {
        if (alive) setState({ items, loading: false, refreshing: false, error: null })
      })
      .catch((err: unknown) => {
        if (!alive || (err as Error)?.name === 'AbortError') return
        setState({
          items: [],
          loading: false,
          refreshing: false,
          error: 'No se pudieron cargar las órdenes de trabajo.',
        })
      })

    return () => {
      alive = false
      ac.abort()
    }
    /*
     * Las dependencias son primitivas y no el objeto `incident` a propósito.
     * La selección se deriva del listado, así que la identidad del objeto
     * cambia en cada refresco aunque los datos sean los mismos, y con `incident`
     * como dependencia cada refresco del listado volvería a pedir todas las
     * órdenes sin motivo. `otCount` sí entra: si aparece una orden nueva, hay
     * que ir a buscarla.
     */
  }, [incidentId, otCount, reloadKey])

  const refresh = useCallback(() => setReloadKey((k) => k + 1), [])

  return { ...state, refresh }
}
